// Formatter comun dată / număr / sumă pentru afișare în UI (docs/design/foundations.md → Formate de date).
// Formatul este fix și independent de limbă și de locale-ul browserului:
//   dată `dd.mm.yyyy` · zi-lună `dd.mm` · oră `HH:mm` · dată-oră `dd.mm.yyyy, HH:mm`
//   număr `12 480` · sumă `12 480,00` (separator de mii: spațiu fix, zecimale cu virgulă).
// Nu folosi metodele `toLocale…String` în componente: Gate UI (`locale-format`) le numără ca încălcări.
// Valorile care nu pot fi interpretate se întorc ca text brut (sau `fallback`), nu se ascund.

const pad = (value) => String(value).padStart(2, "0");
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const NBSP = " ";

function parseDate(value) {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const text = String(value).trim();
  if (!text) return null;
  const dateOnly = DATE_ONLY.exec(text);
  // „yyyy-mm-dd” este o zi calendaristică, nu un moment: se citește local, fără deplasare de fus orar.
  if (dateOnly) {
    const [year, month, day] = [Number(dateOnly[1]), Number(dateOnly[2]), Number(dateOnly[3])];
    const calendar = new Date(year, month - 1, day);
    // „2026-02-30” nu există: `Date` l-ar muta în 02.03.2026; îl lăsăm text, ca să nu apară o altă zi validă.
    const real = calendar.getFullYear() === year && calendar.getMonth() === month - 1 && calendar.getDate() === day;
    return real ? calendar : null;
  }
  const parsed = new Date(text.replace(" ", "T"));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

const raw = (value, fallback) => (fallback !== undefined ? fallback : String(value ?? ""));

export function formatDisplayDate(value, fallback) {
  const date = parseDate(value);
  if (!date) return raw(value, fallback);
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
}

export function formatDisplayDayMonth(value, fallback) {
  const date = parseDate(value);
  if (!date) return raw(value, fallback);
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}`;
}

export function formatDisplayTime(value, { seconds = false, fallback } = {}) {
  const date = parseDate(value);
  if (!date) return raw(value, fallback);
  const base = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  return seconds ? `${base}:${pad(date.getSeconds())}` : base;
}

export function formatDisplayDateTime(value, fallback) {
  const date = parseDate(value);
  if (!date) return raw(value, fallback);
  // O zi fără oră („yyyy-mm-dd”) nu primește un „00:00” inventat.
  if (typeof value === "string" && DATE_ONLY.test(value.trim())) return formatDisplayDate(value);
  return `${formatDisplayDate(date)}, ${formatDisplayTime(date)}`;
}

// Cuvinte localizate de calendar (nu format de dată): numele zilei și „luna anul” din antete.
// Se obțin cu `Intl.DateTimeFormat` numai cu `weekday` / `month`, deci rămân în limba interfeței.
export function formatDisplayWeekday(value, locale, { style = "long" } = {}) {
  const date = parseDate(value);
  if (!date) return "";
  try {
    return new Intl.DateTimeFormat(locale || undefined, { weekday: style }).format(date).replace(/\.$/, "");
  } catch {
    return "";
  }
}

export function formatDisplayMonthYear(value, locale) {
  const date = parseDate(value);
  if (!date) return "";
  try {
    return new Intl.DateTimeFormat(locale || undefined, { month: "long", year: "numeric" }).format(date);
  } catch {
    return `${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
  }
}

function groupThousands(integerPart) {
  return integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

// Număr fără sumă: zecimalele inutile se taie (`12 480`, `0,5`); `maxDecimals` limitează precizia.
export function formatDisplayNumber(value, { maxDecimals = 3, fallback = "" } = {}) {
  if (value === null || value === undefined || value === "") return fallback;
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  const [integerPart, fraction = ""] = Math.abs(num).toFixed(maxDecimals).split(".");
  const trimmed = fraction.replace(/0+$/, "");
  const negative = num < 0 && (Number(integerPart) > 0 || trimmed.length > 0);
  return `${negative ? "-" : ""}${groupThousands(integerPart)}${trimmed ? `,${trimmed}` : ""}`;
}

// Sume în UI: `10 000,00`, independent de locale.
export function formatDisplayAmount(value, decimals = 2) {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  const rounded = Math.abs(num).toFixed(decimals);
  const [integerPart, fraction] = rounded.split(".");
  const negative = num < 0 && Number(rounded) > 0;
  return `${negative ? "-" : ""}${groupThousands(integerPart)}${fraction ? `,${fraction}` : ""}`;
}
